import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { FaArrowUp, FaChevronLeft, FaLock } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { resolveMediaUrl } from '../../../hooks/useStreamBootstrap.js';
import { useFx, useFxCard } from '../../../contexts/FxContext.jsx';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion.js';
import '../../../styles/SetlistRail.css';
import '../../../styles/StreamCta.css';

/** Cards shown when the rail cannot be measured (tests, first paint). */
export const RAIL_PAGE_SIZE = 5;
/** A card's width plus the gap after it, in CSS px (SetlistRail.css). */
export const RAIL_CARD_STEP = 130;
/** Room kept for the pager tile and the divider after the locked card. */
export const RAIL_RESERVED = 86;
/** Room kept for the forward and back pager tiles once the rail is paged (ENG-671). */
export const RAIL_RESERVED_PAGED = 148;

/**
 * How many cards fit a rail `width` px wide, never fewer than one. Paged
 * forward, the back control takes the room the locked card's divider had.
 */
export const railCapacity = (width, paged = false) =>
  Math.max(1, Math.floor((width - (paged ? RAIL_RESERVED_PAGED : RAIL_RESERVED)) / RAIL_CARD_STEP));

/**
 * Whether a listener can boost `entry`: the server says it is boostable, and
 * it is neither the locked up-next song nor #1. The backend refuses both
 * (409 `boost_next_up_locked` / `boost_top_spot`, ENG-807), so neither is
 * offered (ENG-808).
 */
export const canBoostEntry = (entry) => Boolean(entry?.boostable) && !entry.locked && entry.position !== 1;

/**
 * One song on the rail: cover, title, queue number and amount badge. The
 * next-up entry is locked (lock badge, no number). Hovered or focused, the
 * card lifts above the rail and shows the artist, album and year and who
 * requested it (mockup 04, ENG-673). A card that is not a button is still
 * focusable, so keyboard and touch (tap) reach the same state.
 *
 * In boost pick mode (`onBoost`, mockup 05, ENG-675) a boostable card gets a
 * red outline and an "↑ Boost" button, the locked card is dimmed, and amounts
 * are hidden; the button is the card's only control.
 *
 * `yourBoostCents` is what this viewer has boosted the song by; it shows a
 * "YOU +$5" tag (mockup 07, ENG-679) that only the booster sees.
 *
 * With `onVote` (the live queue rail, ENG-806) tapping the art is the
 * upvote: a transparent button covers the card (except the locked one,
 * which ignores taps; nothing is greyed out), and `voteHint` ({seconds})
 * shows "Vote again in Ns" briefly after a tap inside the cooldown.
 */
export const SetlistCard = ({ entry, highlighted = false, onSelect, onBoost, onVote, voteHint = null, yourBoostCents = 0, yours = false }) => {
  const picking = Boolean(onBoost);
  const pickable = picking && canBoostEntry(entry);
  // Tapping the art is the upvote (ENG-806): a transparent button over the
  // card. The locked up-next song has none, so a tap on it does nothing.
  const votable = Boolean(onVote) && !picking && !onSelect && !entry.locked;
  const { t, format } = useLocale();
  const artwork = resolveMediaUrl(entry.artworkUrl);
  const label = entry.locked
    ? t('stream.setlist.lockedLabel', { title: entry.title })
    : t('stream.setlist.entryLabel', { title: entry.title, position: format.number(entry.position) });
  const Tag = onSelect && !picking ? 'button' : 'div';
  const infoId = useId();
  const albumLine = entry.album && entry.releaseYear
    ? t('stream.setlist.albumYear', { album: entry.album, year: String(entry.releaseYear) })
    : entry.album || (entry.releaseYear ? String(entry.releaseYear) : '');

  return (
    <Tag
      {...(Tag === 'button' ? { type: 'button', onClick: () => onSelect(entry) } : picking || votable ? {} : { tabIndex: 0 })}
      className={`setlist-card${entry.locked ? ' setlist-card--locked' : ''}${highlighted ? ' setlist-card--highlighted' : ''}${pickable ? ' setlist-card--pickable' : ''}${picking && !pickable ? ' setlist-card--dimmed' : ''}`}
      data-testid="setlist-card"
      data-entry-id={entry.entryId}
      aria-describedby={infoId}
    >
      <span className="setlist-card-sr">{label}</span>
      {!picking && (yours || yourBoostCents > 0) && (
        <span className="setlist-card-tags">
          {yours && <span className="setlist-card-yours" data-testid="setlist-card-yours">{t('stream.setlist.yours')}</span>}
          {yourBoostCents > 0 && (
            <span className="setlist-card-you" data-testid="setlist-card-you">
              {t('stream.setlist.yourBoost', { amount: format.usd(yourBoostCents / 100, { cents: 'auto' }) })}
            </span>
          )}
        </span>
      )}
      {artwork
        ? <img src={artwork} alt="" className="setlist-card-cover" />
        : <span className="setlist-card-cover setlist-card-cover--blank" aria-hidden="true">♪</span>}
      {!picking && entry.amount > 0 && (
        <span className="setlist-card-amount" aria-hidden="true">{format.usd(entry.amount, { cents: 'never' })}</span>
      )}
      {entry.locked ? (
        <span className="setlist-card-badge setlist-card-badge--lock" aria-hidden="true"><FaLock /></span>
      ) : (
        <span className="setlist-card-badge" aria-hidden="true">{format.number(entry.position)}</span>
      )}
      <span className="setlist-card-title" aria-hidden="true">{entry.title}</span>
      {votable && (
        <button
          type="button"
          className="setlist-card-vote"
          data-testid="setlist-card-vote"
          onClick={() => onVote(entry)}
          aria-label={t('stream.setlist.voteLabel', { title: entry.title })}
          aria-describedby={infoId}
        />
      )}
      {votable && voteHint && (
        <span key={voteHint.at} className="setlist-card-hint" data-testid="setlist-card-hint" role="status">
          {t('stream.setlist.voteCooldown', { seconds: format.number(voteHint.seconds) })}
        </span>
      )}
      {pickable && (
        <button
          type="button"
          className="setlist-card-boost stream-cta"
          data-testid="setlist-card-boost"
          onClick={() => onBoost(entry)}
          aria-label={t('stream.boost.pickLabel', { title: entry.title })}
        >
          <FaArrowUp aria-hidden="true" />
          <span aria-hidden="true">{t('stream.boost.pick')}</span>
        </button>
      )}
      {/* The card's description, not part of its name (aria-describedby reads it though hidden). */}
      <span className="setlist-card-info" data-testid="setlist-card-info" id={infoId} aria-hidden="true">
        {entry.artist && <span className="setlist-card-info-line setlist-card-info-artist">{entry.artist}</span>}
        {albumLine && <span className="setlist-card-info-line">{albumLine}</span>}
        {entry.requestedBy && (
          <span className="setlist-card-info-line">{t('stream.setlist.requestedBy', { name: entry.requestedBy })}</span>
        )}
      </span>
    </Tag>
  );
};

/**
 * One slot of the rail: the list item and, inside it, the wrapper the
 * SongShake FX engine moves (ENG-804). Under an FxContext the slot registers
 * with the page's stage as the song's effects card: the item is the card (the
 * engine sets its z-index while it is lit or moving), the wrapper is the inner
 * element (the engine owns its transform, so the card's own hover lift, on
 * `.setlist-card`, still composes with it) and the cover art is the box the
 * effects wrap (the engine owns its filter; the pick-mode dimming filter sits
 * on `.setlist-card`, not the art). Effects are drawn on the stage's canvases,
 * never inside the card, so the card keeps its layout.
 */
const RailSlot = ({ entry, fxOn, children }) => {
  const itemRef = useRef(null);
  const innerRef = useRef(null);
  useFxCard(fxOn ? entry.entryId : null, {
    elRef: itemRef,
    innerRef,
    coverSelector: '.setlist-card-cover',
    artKey: entry.artworkUrl ?? null,
  });
  return (
    <li
      ref={itemRef}
      className={`setlist-rail-item${entry.locked ? ' setlist-rail-item--locked' : ''}${fxOn ? ' ssfx-card' : ''}`}
    >
      <div ref={innerRef} className="setlist-rail-fx">{children}</div>
    </li>
  );
};

/**
 * The rail's measured width, so it shows as many cards as fit (mockups 01
 * and 02, ENG-669): collapsing the now-playing card shows more of the queue
 * instead of stretching or clipping cards. Null where nothing can be
 * measured; the rail then falls back to its `pageSize`.
 */
const useRailWidth = (ref) => {
  const [width, setWidth] = useState(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const measure = (w) => {
      if (w > 0) setWidth(w);
    };
    measure(node.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => measure(entry.contentRect.width));
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return width;
};

/**
 * Animates each card from its last position to its new one when `order`
 * changes (FLIP), using the Web Animations API so no transform is left in the
 * stylesheet. Cards new to the view simply appear. With `enabled` off (a
 * boost is moving the cards itself, ENG-808) the positions are still noted,
 * so the next glide starts from where the cards really are.
 */
const useReorderGlide = (ref, order, enabled = true) => {
  const rects = useRef(new Map());
  // The device's reduce-motion setting, followed live (ENG-837). The
  // reduced-motion stylesheet cannot reach `element.animate`, so it is checked here.
  const reducedMotion = usePrefersReducedMotion();
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const reduce = !enabled || reducedMotion;
    const next = new Map();
    node.querySelectorAll('[data-entry-id]').forEach((card) => {
      const rect = card.getBoundingClientRect();
      const id = card.getAttribute('data-entry-id');
      next.set(id, rect);
      const before = rects.current.get(id);
      const dx = before ? before.left - rect.left : 0;
      if (!reduce && Math.abs(dx) > 1 && typeof card.animate === 'function') {
        card.animate([{ transform: `translateX(${dx}px)` }, { transform: 'none' }], { duration: 400, easing: 'ease-out' });
      }
    });
    rects.current = next;
  }, [ref, order, enabled, reducedMotion]);
};

/**
 * The setlist rail under the player (mockups 01–03, ENG-667/669/671): the
 * queue in play order as one strip — the locked next-up card, a divider, then
 * the numbered cards — viewed a window at a time. The window is an offset into
 * the strip, so a narrower or wider rail (the now-playing card collapsing)
 * changes how many cards show but never which card leads, and a live queue
 * change keeps the offset, clamped so the last card still meets the edge.
 * Paged forward, an "N back" control leads the rail (N counts the locked
 * card too); at the end of the queue the forward control goes away.
 *
 * When the order changes (a boost, ENG-679), cards that stay in view glide
 * from where they were to where they are now, unless the viewer prefers
 * reduced motion. `yourBoosts` maps entry ids to what this viewer added.
 * `glide={false}` turns that off while a boost animation moves the cards
 * (ENG-808, `useFxBoosts`).
 */
const SetlistRail = ({ entries = [], loading = false, error = false, pageSize = RAIL_PAGE_SIZE, onSelectEntry, onBoostEntry, onVoteEntry, voteHint = null, highlightedEntryId = null, yourBoosts = null, yourEntryIds = null, glide = true }) => {
  const { t, format } = useLocale();
  const railRef = useRef(null);
  const width = useRailWidth(railRef);
  // The stream page's queue rail draws SongShake FX (ENG-804): it is the
  // stage's row (between the two canvas layers, shaken on a boost release).
  const fx = useFx();
  const fxOn = Boolean(fx);
  const fxRowRef = fx?.rowRef ?? null;
  const setRailRef = useCallback((node) => {
    railRef.current = node;
    fxRowRef?.(node);
  }, [fxRowRef]);
  const [offset, setOffset] = useState(0);

  const locked = entries.find((e) => e.locked) ?? null;
  const strip = locked ? [locked, ...entries.filter((e) => !e.locked)] : entries;
  // How many cards fit at the start (pager only) and once paged (back too).
  const startCapacity = width == null ? pageSize : railCapacity(width);
  const pagedCapacity = width == null ? pageSize : railCapacity(width, true);
  // The last window, aligned to the rail edge; nothing to page when it all fits.
  const lastOffset = strip.length <= startCapacity ? 0 : Math.max(1, strip.length - pagedCapacity);
  const start = Math.min(offset, lastOffset);
  const capacity = start > 0 ? pagedCapacity : startCapacity;
  const visible = strip.slice(start, start + capacity);
  const remaining = strip.length - (start + visible.length);
  const goForward = () => setOffset(Math.min(start + capacity, lastOffset));
  const goBack = () => setOffset(Math.max(0, start - pagedCapacity));
  const order = visible.map((entry) => entry.entryId).join(',');
  useReorderGlide(railRef, order, glide);

  let body;
  if (loading) {
    body = <p>{t('stream.queue.loading')}</p>;
  } else if (error && entries.length === 0) {
    body = <p>{t('stream.queue.loadError')}</p>;
  } else if (entries.length === 0) {
    body = <p>{t('stream.queue.empty')}</p>;
  }

  return (
    <div className={`setlist-rail${body ? ' setlist-rail--status' : ''}${fxOn ? ' ssfx-row' : ''}`} data-testid="setlist-rail" ref={setRailRef}>
      {body ?? (
        <>
          {start > 0 && (
            <button
              type="button"
              className="setlist-rail-pager setlist-rail-back"
              data-testid="setlist-back"
              onClick={goBack}
            >
              <FaChevronLeft className="setlist-rail-pager-icon rtl-mirror" aria-hidden="true" />
              {t('stream.setlist.back', { count: start, formattedCount: format.number(start) })}
            </button>
          )}
          <ol className="setlist-rail-list" aria-label={t('stream.queue.listLabel')}>
            {visible.map((entry, index) => (
              <React.Fragment key={entry.entryId}>
                <RailSlot entry={entry} fxOn={fxOn}>
                  <SetlistCard
                    entry={entry}
                    onSelect={onSelectEntry}
                    onBoost={onBoostEntry}
                    onVote={onVoteEntry}
                    voteHint={voteHint?.entryId === entry.entryId ? voteHint : null}
                    highlighted={highlightedEntryId != null && entry.entryId === highlightedEntryId}
                    yourBoostCents={yourBoosts?.[entry.entryId] ?? 0}
                    yours={Boolean(yourEntryIds?.includes(entry.entryId))}
                  />
                </RailSlot>
                {entry.locked && index < visible.length - 1 && (
                  <li className="setlist-rail-divider" aria-hidden="true" />
                )}
              </React.Fragment>
            ))}
          </ol>
          {remaining > 0 && (
            <button
              type="button"
              className="setlist-rail-pager setlist-rail-more"
              data-testid="setlist-more"
              onClick={goForward}
            >
              {t('stream.setlist.more', { count: remaining, formattedCount: format.number(remaining) })}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default SetlistRail;
